import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, serverTimestamp, Timestamp, orderBy } from 'firebase/firestore';

const STATUS_COLORS = {
  scheduled:    { bg: '#fff7ed', color: '#F97316', label: 'Scheduled' },
  driver_coming:{ bg: '#eff6ff', color: '#3b82f6', label: 'Driver En Route' },
  checked_in:   { bg: '#fdf4ff', color: '#9333ea', label: 'Vehicle Arrived' },
  in_service:   { bg: '#f0fdf4', color: '#16a34a', label: 'In Service' },
  ready:        { bg: '#ecfdf5', color: '#059669', label: 'Ready for Pickup' },
  completed:    { bg: '#f9fafb', color: '#6b7280', label: 'Completed' },
};

export default function TodaysJobsTab({ user, centerData }) {
  const [jobs, setJobs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!centerData?.id) { setLoading(false); return; }
    const start = new Date(); start.setHours(0,0,0,0);
    const end   = new Date(); end.setHours(23,59,59,999);
    const q = query(
      collection(db, 'washRequests'),
      where('serviceCenterId', '==', centerData.id),
      where('scheduledAt', '>=', Timestamp.fromDate(start)),
      where('scheduledAt', '<=', Timestamp.fromDate(end)),
      orderBy('scheduledAt', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [centerData]);

  const updateStatus = async (jobId, newStatus) => {
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'washRequests', jobId), {
        centerStatus: newStatus,
        [`centerStatusHistory.${newStatus}`]: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      if (selected?.id === jobId) setSelected(j => ({ ...j, centerStatus: newStatus }));
    } catch (e) { alert('Error: ' + e.message); }
    finally { setUpdating(false); }
  };

  const getNextAction = (job) => {
    switch (job.centerStatus) {
      case 'scheduled':     return { label: 'Mark Vehicle Arrived', next: 'checked_in', color: '#9333ea' };
      case 'checked_in':    return { label: 'Start Service', next: 'in_service', color: '#16a34a' };
      case 'in_service':    return { label: 'Mark Ready for Pickup', next: 'ready', color: '#059669' };
      case 'ready':         return { label: 'Mark Completed', next: 'completed', color: '#6b7280' };
      default: return null;
    }
  };

  const filtered = filter === 'all' ? jobs : jobs.filter(j => j.centerStatus === filter);
  const counts = { all: jobs.length, scheduled: 0, checked_in: 0, in_service: 0, ready: 0, completed: 0 };
  jobs.forEach(j => { if (counts[j.centerStatus] !== undefined) counts[j.centerStatus]++; });

  return (
    <div style={{ padding: 32 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900 }}>Today's Jobs</h1>
        <p style={{ color: '#6b7280', marginTop: 4 }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
      </div>

      {/* Status filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {[['all','All'], ['scheduled','Scheduled'], ['checked_in','Arrived'], ['in_service','In Service'], ['ready','Ready'], ['completed','Done']].map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)} style={{ padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: filter === key ? 800 : 600, background: filter === key ? '#F97316' : '#f3f4f6', color: filter === key ? '#fff' : '#374151' }}>
            {label} ({counts[key] ?? jobs.length})
          </button>
        ))}
      </div>

      {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : filtered.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔧</div>
          <h3 style={{ fontWeight: 800, marginBottom: 6 }}>No jobs {filter !== 'all' ? 'with this status' : 'today'}</h3>
          <p style={{ color: '#6b7280' }}>Check back later or adjust the filter above</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {filtered.map(job => {
            const sc = STATUS_COLORS[job.centerStatus] || STATUS_COLORS.scheduled;
            const nextAction = getNextAction(job);
            const time = job.scheduledAt?.toDate?.()?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            return (
              <div key={job.id} style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: `4px solid ${sc.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 16, fontWeight: 900, color: '#111827' }}>{time}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color }}>{sc.label}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>{(job.selectedServices || []).join(', ') || 'Vehicle Service'}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      {job.vehicleColor} {job.vehicleMake} {job.vehicleModel}
                      {job.licensePlate ? ` · ${job.licensePlate}` : ''}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Driver: {job.driverName || 'Not assigned'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 900, color: '#111827' }}>${(job.quotedCenterAmount || 0).toFixed(2)}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Job #{job.id.slice(-6).toUpperCase()}</div>
                  </div>
                </div>
                {nextAction && (
                  <button
                    onClick={() => updateStatus(job.id, nextAction.next)}
                    disabled={updating}
                    style={{ width: '100%', padding: '10px', background: nextAction.color, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                  >
                    {updating ? 'Updating...' : nextAction.label}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}